<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Dialog Component
      p.text--secondary Modal dialogs for displaying content, forms, or confirmations. Available in normal and fullscreen modes with various configuration options.

    // Normal Dialog Examples
    .component-section.mb-8#normal-dialogs
      h3.mb-4.cursor-pointer(@click="navigateToSection('normal-dialogs')") Normal Dialog Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in normalVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Dialog Implementation
            .d-flex.align-center.gap-2
              yz-btn(:label="variation.buttonLabel" @click="openDialog(variation.id)")

            // Dialog Component
            yz-dialog(v-model="dialogs[variation.id]" persistent scrollable max-width="500")
              yz-card
                yz-dialog-normal-header(:title="variation.dialogTitle" @close="closeDialog(variation.id)")
                yz-dialog-normal-content
                  p {{ variation.dialogContent }}
                  template(v-if="variation.longContent")
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    p Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
                    p Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
                    p Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium.
                    p Totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt.
                    p Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur.
                    p Magni dolores eos qui ratione voluptatem sequi nesciunt neque porro quisquam est qui dolorem.
                    p Ipsum quia dolor sit amet, consectetur, adipisci velit, sed quia non numquam eius modi tempora.
                    p Incidunt ut labore et dolore magnam aliquam quaerat voluptatem ut enim ad minima veniam.
                    p Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae.
                    p Consequatur aut perferendis doloribus asperiores repellat voluptatem accusantium doloremque.
                    p At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium.
                    p Voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati.
                    p Cupiditate non provident, similique sunt in culpa qui officia deserunt mollitia animi.
                yz-dialog-normal-actions(@save="closeDialog(variation.id)" @cancel="closeDialog(variation.id)")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Fullscreen Dialog Examples
    .component-section.mb-8#fullscreen-dialogs
      h3.mb-4.cursor-pointer(@click="navigateToSection('fullscreen-dialogs')") Fullscreen Dialog Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in fullscreenVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}

            // Dialog Implementation
            .d-flex.align-center.gap-2
              yz-btn(:label="variation.buttonLabel" @click="openDialog(variation.id)")

            // Dialog Component
            yz-dialog(v-model="dialogs[variation.id]" max-width="1000" fullscreen persistent fluid scrollable)
              yz-card
                yz-dialog-fullscreen-header(:title="variation.dialogTitle" @cancel="closeDialog(variation.id)" @save="closeDialog(variation.id)" @back="closeDialog(variation.id)")
                yz-dialog-fullscreen-content.pa-6
                  p {{ variation.dialogContent }}
                  p(v-if="variation.longContent") {{ variation.longContent }}
                  .mt-4(v-if="variation.showExtraContent")
                    h4.mb-2 Additional Content
                    p This is additional content to demonstrate scrollable behavior in fullscreen dialogs.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    p Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Normal Header Examples
    .component-section.mb-8#normal-header-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('normal-header-examples')") Normal Header Examples
      .variations-grid
        .variation-item.mb-6(v-for="example in normalHeaderExamples" :key="example.name" :id="example.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(example.id)") {{ example.name }}
            p.text--secondary.caption {{ example.description }}

          .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
            yz-card(color="white")
              yz-dialog-normal-header(v-if="example.id !== 'normal-header-slots'" v-bind="example.props")
              yz-dialog-normal-header(v-else)
                template(#title)
                  .d-flex.align-center
                    yz-icon.mr-2(icon="mdi-information" color="info")
                    .yz-heading-8 Custom Slot Title
                template(#actions)
                  .d-flex.align-center.gap-4
                    yz-btn(icon="mdi-help-circle" size="md" outlined)
                    yz-btn(icon="mdi-close" size="md")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === example.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === example.code ? 'success' : ''" @click="copyCode(example.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ example.code }}

    // Normal Actions Examples
    .component-section.mb-8#normal-actions-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('normal-actions-examples')") Normal Actions Examples
      .variations-grid
        .variation-item.mb-6(v-for="example in normalActionsExamples" :key="example.name" :id="example.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(example.id)") {{ example.name }}
            p.text--secondary.caption {{ example.description }}

          .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
            yz-card(color="white")
              yz-dialog-normal-actions(v-if="example.id !== 'actions-with-slot'" v-bind="example.props")
              yz-dialog-normal-actions(v-else)
                .d-flex.gap-3
                  yz-btn(label="Maybe Later" outlined)
                  yz-btn(label="Delete" color="danger")
                  yz-btn(label="Save & Continue" color="primary")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === example.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === example.code ? 'success' : ''" @click="copyCode(example.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ example.code }}

    // Fullscreen Header Examples
    .component-section.mb-8#fullscreen-header-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('fullscreen-header-examples')") Fullscreen Header Examples
      .variations-grid
        .variation-item.mb-6(v-for="example in fullscreenHeaderExamples" :key="example.name" :id="example.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(example.id)") {{ example.name }}
            p.text--secondary.caption {{ example.description }}

          .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 1500px;")
            yz-card(color="white")
              yz-dialog-fullscreen-header(v-if="example.id !== 'with-slots'" v-bind="example.props" :class="example.id === 'custom-styling' ? 'px-8 py-4' : ''")
              yz-dialog-fullscreen-header(v-else)
                template(#left)
                  yz-btn(icon="mdi-menu" outlined size="md")
                template(#title)
                  .d-flex.align-center
                    yz-icon.mr-2(icon="mdi-star" color="warning")
                    .yz-heading-6 Custom Title
                template(#actions)
                  .d-flex.align-center.gap-4
                    yz-btn(label="Draft" outlined)
                    yz-btn(label="Publish")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === example.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === example.code ? 'success' : ''" @click="copyCode(example.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ example.code }}

    // Fullscreen Content Examples
    .component-section.mb-8#fullscreen-content-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('fullscreen-content-examples')") Fullscreen Content Examples
      .variations-grid
        .variation-item.mb-6(v-for="example in fullscreenContentExamples" :key="example.name" :id="example.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(example.id)") {{ example.name }}
            p.text--secondary.caption {{ example.description }}

          .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 1500px;")
            yz-card(color="white")
              yz-dialog-fullscreen-content(v-bind="example.props")
                div(v-html="example.content" style="white-space: pre-line;")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === example.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === example.code ? 'success' : ''" @click="copyCode(example.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ example.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API

      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in componentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h4.mb-3 Slots
      .slots-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr(v-for="slot in componentSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}

      h4.mb-3 Common Use Cases
      .use-cases-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Scenario
                th Recommended Approach
                th Example
            tbody
              tr(v-for="useCase in useCases" :key="useCase.scenario")
                td.font-weight-medium.text-no-wrap {{ useCase.scenario }}
                td.text--secondary {{ useCase.approach }}
                td.single-line {{ useCase.example }}

      h4.mb-3 Implementation Notes
      .notes-section
        v-alert(type="info" outlined)
          ul.mb-0
            li
              strong Persistent: 
              | Use persistent prop to prevent closing by clicking outside or pressing escape
            li
              strong Fluid: 
              | Fluid prop makes dialog responsive to content width
            li
              strong Scrollable: 
              | Enable scrollable for long content that exceeds viewport height
            li
              strong Fullscreen: 
              | Use fullscreen for complex forms or detailed content views
            li
              strong Max Width: 
              | Set max-width for fullscreen dialogs to control content width on large screens
            li
              strong Smart Padding: 
              | YzDialogFullscreenHeader automatically applies px-6 py-3 padding unless parent component already has padding classes (pa-, pt-, px-, etc.), preventing conflicting styles

      h4.mb-3 YzDialogFullscreenHeader API
      h5.mb-2 Props
      .props-table.mb-4
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in fullscreenHeaderProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h5.mb-2 Slots
      .slots-table.mb-4
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr
                td.font-weight-medium.text-no-wrap left
                td.single-line Custom content for left side (replaces default back button). Falls back to back button if not provided
              tr
                td.font-weight-medium.text-no-wrap title
                td.single-line Custom content for title area (replaces title prop)
              tr
                td.font-weight-medium.text-no-wrap actions
                td.single-line Custom action buttons (replaces default Cancel/Save buttons)

      h5.mb-2 Events
      .events-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Description
            tbody
              tr(v-for="event in fullscreenHeaderEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.single-line {{ event.description }}

      h4.mb-3 YzDialogNormalHeader API
      h5.mb-2 Props
      .props-table.mb-4
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in normalHeaderProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h5.mb-2 Slots
      .slots-table.mb-4
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr
                td.font-weight-medium.text-no-wrap left
                td.single-line Empty slot for left side content (no default content)
              tr
                td.font-weight-medium.text-no-wrap title
                td.single-line Custom content for title area (replaces title prop)
              tr
                td.font-weight-medium.text-no-wrap actions
                td.single-line Custom action buttons (replaces default close button)

      h5.mb-2 Events
      .events-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Description
            tbody
              tr(v-for="event in normalHeaderEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.single-line {{ event.description }}

      h4.mb-3 YzDialogNormalActions API
      h5.mb-2 Props
      .props-table.mb-4
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in normalActionsProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h5.mb-2 Slots
      .slots-table.mb-4
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr
                td.font-weight-medium.text-no-wrap default
                td.single-line Custom action buttons (replaces default cancel/save buttons)

      h5.mb-2 Events
      .events-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Description
            tbody
              tr(v-for="event in normalActionsEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.single-line {{ event.description }}

      h4.mb-3 YzDialogFullscreenContent API
      h5.mb-2 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in fullscreenContentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      dialogs: {}
    }
  },
  computed: {
    normalVariations() {
      return [
        {
          name: 'Basic Dialog',
          id: 'basic-dialog',
          description: 'Standard dialog with persistent and scrollable properties',
          label: 'Basic Dialog',
          hint: 'Default modal behavior',
          buttonLabel: 'Open Basic Dialog',
          dialogTitle: 'Basic Dialog',
          dialogContent: 'This is a basic dialog example with standard configuration.',
          code: `yz-btn(label="Open Basic Dialog" @click="openDialog('basic-dialog')")

yz-dialog(v-model="dialogs['basic-dialog']" persistent scrollable width="500")
  yz-card
    yz-dialog-normal-header(title="Basic Dialog" @close="closeDialog('basic-dialog')")
    yz-dialog-normal-content
      p This is a basic dialog example with standard configuration.
    yz-dialog-normal-actions(
      save-label="Confirm"
      @save="closeDialog('basic-dialog')" 
      @cancel="closeDialog('basic-dialog')"
    )`
        },
        {
          name: 'Scrollable Dialog',
          id: 'scrollable-dialog',
          description: 'Dialog with very long content that demonstrates scrollable behavior',
          label: 'Scrollable Dialog',
          hint: 'Long content with scroll',
          buttonLabel: 'Open Scrollable',
          dialogTitle: 'Scrollable Content Dialog',
          dialogContent: 'This dialog contains very long content to demonstrate the scrollable behavior when content exceeds the viewport height.',
          longContent: true,
          code: `yz-btn(label="Open Scrollable" @click="openDialog('scrollable-dialog')")

yz-dialog(v-model="dialogs['scrollable-dialog']" persistent scrollable width="500")
  yz-card
    yz-dialog-normal-header(title="Scrollable Content Dialog" @close="closeDialog('scrollable-dialog')")
    yz-dialog-normal-content
      p This dialog contains very long content to demonstrate the scrollable behavior.
      p Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor...
      p Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut...
      // ... many more paragraphs of content for scrolling demonstration
    yz-dialog-normal-actions(
      save-label="Confirm"
      @save="closeDialog('scrollable-dialog')" 
      @cancel="closeDialog('scrollable-dialog')"
    )`
        }
      ]
    },
    fullscreenVariations() {
      return [
        {
          name: 'Fullscreen Dialog',
          id: 'fullscreen-dialog',
          description: 'Fullscreen dialog that covers the entire viewport',
          label: 'Fullscreen Dialog',
          hint: 'Full viewport coverage',
          buttonLabel: 'Open Fullscreen',
          dialogTitle: 'Fullscreen Dialog',
          dialogContent: 'This is a fullscreen dialog that covers the entire viewport without width constraints.',
          showExtraContent: true,
          code: `yz-btn(label="Open Fullscreen" @click="openDialog('fullscreen-dialog')")

yz-dialog(v-model="dialogs['fullscreen-dialog']" max-width="1000" fullscreen persistent fluid scrollable)
  yz-card
    yz-dialog-fullscreen-header(title="Fullscreen Dialog" @cancel="closeDialog('fullscreen-dialog')" @save="closeDialog('fullscreen-dialog')" @back="closeDialog('fullscreen-dialog')")
    yz-dialog-fullscreen-content.pa-6
      p This is a fullscreen dialog that covers the entire viewport without width constraints.
      // Additional content for demonstration`
        }
      ]
    },
    normalHeaderExamples() {
      return [
        {
          name: 'Basic Normal Header',
          id: 'basic-normal-header',
          description: 'Default normal header with title and close button',
          props: {
            title: 'Basic Normal Header'
          },
          code: `yz-dialog-normal-header(title="Basic Normal Header" @close="closeDialog")`
        },
        {
          name: 'Custom Close Icon',
          id: 'custom-close-icon',
          description: 'Normal header with custom close icon',
          props: {
            title: 'Custom Close Icon',
            closeIcon: 'mdi-window-close'
          },
          code: `yz-dialog-normal-header(
  title="Custom Close Icon"
  close-icon="mdi-window-close"
  @close="closeDialog"
)`
        },
        {
          name: 'Disabled Close',
          id: 'normal-header-disabled',
          description: 'Normal header with close button disabled',
          props: {
            title: 'Cannot Close',
            disableClose: true
          },
          code: `yz-dialog-normal-header(
  title="Cannot Close"
  :disable-close="isProcessing"
  @close="closeDialog"
)`
        },
        {
          name: 'Custom Divider Color',
          id: 'normal-header-divider',
          description: 'Normal header with custom divider color',
          props: {
            title: 'Custom Divider',
            dividerColor: 'yz-blue-500'
          },
          code: `yz-dialog-normal-header(
  title="Custom Divider"
  divider-color="yz-blue-500"
  @close="closeDialog"
)`
        },
        {
          name: 'With Slots',
          id: 'normal-header-slots',
          description: 'Normal header using slots for custom content',
          props: {},
          code: `yz-dialog-normal-header(@close="closeDialog")
  template(#title)
    .d-flex.align-center
      yz-icon.mr-2(icon="mdi-information" color="info")
      .yz-heading-8 Custom Slot Title
  template(#actions)
    yz-btn(icon="mdi-help-circle" size="md" outlined @click="showHelp")
    yz-btn(icon="mdi-close" size="md" @click="closeDialog")`
        }
      ]
    },
    normalActionsExamples() {
      return [
        {
          name: 'Basic Actions',
          id: 'basic-actions',
          description: 'Default actions with save and cancel buttons',
          props: {},
          code: `yz-dialog-normal-actions(
  @save="handleSave"
  @cancel="handleCancel"
)`
        },
        {
          name: 'Custom Labels',
          id: 'custom-labels',
          description: 'Actions with custom button labels',
          props: {
            saveLabel: 'Confirm',
            cancelLabel: 'Discard'
          },
          code: `yz-dialog-normal-actions(
  save-label="Confirm"
  cancel-label="Discard"
  @save="handleSave"
  @cancel="handleCancel"
)`
        },
        {
          name: 'Loading State',
          id: 'loading-actions',
          description: 'Actions with loading state - disables cancel button and shows loading on save button',
          props: {
            saveLabel: 'Saving...',
            loading: true
          },
          code: `yz-dialog-normal-actions(
  save-label="Save"
  :loading="isSubmitting"
  @save="handleSave"
  @cancel="handleCancel"
)`
        },
        {
          name: 'Custom Styling',
          id: 'custom-actions-styling',
          description: 'Actions with custom gap, justify, color, and underline styling',
          props: {
            saveLabel: 'Publish',
            cancelLabel: 'Draft',
            cancelActionUnderline: true,
            cancelActionColor: 'yz-blue-500',
            actionGap: '24px',
            justify: 'start'
          },
          code: `yz-dialog-normal-actions(
  save-label="Publish"
  cancel-label="Draft"
  cancel-action-underline
  cancel-action-color="yz-blue-500"
  action-gap="24px"
  justify="start"
  @save="handleSave"
  @cancel="handleCancel"
)`
        },
        {
          name: 'Disable Save Only',
          id: 'actions-disable-save',
          description: 'Actions with save button disabled (useful for validation errors)',
          props: {
            saveLabel: 'Save',
            cancelLabel: 'Cancel',
            disableSave: true
          },
          code: `yz-dialog-normal-actions(
  save-label="Save"
  cancel-label="Cancel"
  :disable-save="hasValidationErrors"
  @save="handleSave"
  @cancel="handleCancel"
)`
        },
        {
          name: 'Disable Both Buttons',
          id: 'actions-disable-both',
          description: 'Actions with both buttons disabled (rare use case)',
          props: {
            saveLabel: 'Save',
            cancelLabel: 'Cancel',
            disableCancel: true,
            disableSave: true
          },
          code: `yz-dialog-normal-actions(
  save-label="Save"
  cancel-label="Cancel"
  :disable-cancel="isProcessing"
  :disable-save="isProcessing"
  @save="handleSave"
  @cancel="handleCancel"
)`
        },
        {
          name: 'With Custom Slot',
          id: 'actions-with-slot',
          description: 'Actions using slot for completely custom buttons',
          props: {},
          code: `yz-dialog-normal-actions(@save="handleSave" @cancel="handleCancel")
  .d-flex.gap-3
    yz-btn(label="Maybe Later" outlined color="secondary" @click="postpone")
    yz-btn(label="Delete" color="danger" @click="handleDelete")
    yz-btn(label="Save & Continue" color="primary" @click="handleSave")`
        }
      ]
    },
    fullscreenHeaderExamples() {
      return [
        {
          name: 'Basic Header',
          id: 'basic-header',
          description: 'Default fullscreen header with title and actions',
          label: 'Basic Header',
          props: {
            title: 'Basic Header'
          },
          code: `yz-dialog-fullscreen-header(title="Basic Header" @back="closeDialog" @cancel="closeDialog" @save="closeDialog")`
        },
        {
          name: 'Custom Actions',
          id: 'custom-actions',
          description: 'Header with customized action labels',
          label: 'Custom Actions',
          props: {
            title: 'Custom Actions',
            cancelActionLabel: 'Discard',
            saveActionLabel: 'Publish'
          },
          code: `yz-dialog-fullscreen-header(
  title="Custom Actions"
  cancel-action-label="Discard"
  save-action-label="Publish"
  @back="closeDialog"
  @cancel="closeDialog"
  @save="closeDialog"
)`
        },
        {
          name: 'No Back Button',
          id: 'no-back-button',
          description: 'Header with only title and actions, no back button',
          label: 'No Back Button',
          props: {
            title: 'No Back Button',
            leftIcon: null,
            cancelActionLabel: 'Cancel',
            saveActionLabel: 'Next'
          },
          code: `yz-dialog-fullscreen-header(
  title="No Back Button"
  :left-icon="null"
  cancel-action-label="Cancel"
  save-action-label="Next"
  @save="closeDialog"
  @cancel="closeDialog"
)`
        },
        {
          name: 'Custom Styling',
          id: 'custom-styling',
          description: 'Header with custom padding (via class), gap, and custom icon',
          label: 'Custom Styling',
          props: {
            title: 'Custom Styling',
            actionGap: '16px',
            leftIcon: 'mdi-close',
            cancelActionLabel: 'Discard',
            saveActionLabel: 'Publish'
          },
          code: `yz-dialog-fullscreen-header.px-8.py-4(
  title="Custom Styling"
  left-icon="mdi-close"
  action-gap="16px"
  cancel-action-label="Discard"
  save-action-label="Publish"
  @back="closeDialog"
  @cancel="closeDialog"
  @save="closeDialog"
)`
        },
        {
          name: 'Loading State',
          id: 'loading-state',
          description: 'Header with loading state - shows spinner on save button and disables cancel',
          label: 'Loading State',
          props: {
            title: 'Loading State',
            loading: true,
            cancelActionLabel: 'Cancel',
            saveActionLabel: 'Saving...'
          },
          code: `yz-dialog-fullscreen-header(
  title="Loading State"
  :loading="isSubmitting"
  cancel-action-label="Cancel"
  save-action-label="Save"
  @back="closeDialog"
  @cancel="closeDialog"
  @save="saveData"
)`
        },
        {
          name: 'Disable Save Only',
          id: 'disable-save-only',
          description: 'Header with save button disabled (useful during errors or validation)',
          label: 'Disable Save Only',
          props: {
            title: 'Disable Save Only',
            disableSave: true,
            cancelActionLabel: 'Close',
            saveActionLabel: 'Save'
          },
          code: `yz-dialog-fullscreen-header(
  title="Disable Save Only"
  :disable-save="hasErrors"
  cancel-action-label="Close"
  save-action-label="Save"
  @back="closeDialog"
  @cancel="closeDialog"
  @save="saveData"
)`
        },
        {
          name: 'Disable Back Button',
          id: 'disable-back',
          description: 'Header with back button disabled while keeping actions enabled',
          label: 'Disable Back Button',
          props: {
            title: 'Disable Back Button',
            disableBack: true,
            cancelActionLabel: 'Cancel',
            saveActionLabel: 'Save'
          },
          code: `yz-dialog-fullscreen-header(
  title="Disable Back Button"
  :disable-back="isInTransaction"
  cancel-action-label="Cancel"
  save-action-label="Save"
  @back="closeDialog"
  @cancel="closeDialog"
  @save="saveData"
)`
        },
        {
          name: 'Disable All Actions',
          id: 'disable-all',
          description: 'Header with all buttons disabled (back, cancel, and save)',
          label: 'Disable All Actions',
          props: {
            title: 'Disable All Actions',
            disableBack: true,
            disableCancel: true,
            disableSave: true,
            cancelActionLabel: 'Cancel',
            saveActionLabel: 'Save'
          },
          code: `yz-dialog-fullscreen-header(
  title="Disable All Actions"
  :disable-back="isLocked"
  :disable-cancel="isLocked"
  :disable-save="isLocked"
  @back="closeDialog"
  @cancel="closeDialog"
  @save="saveData"
)`
        },
        {
          name: 'With Slots',
          id: 'with-slots',
          description: 'Header using slots for custom content',
          label: 'With Slots',
          props: {},
          code: `yz-dialog-fullscreen-header
  template(#left)
    yz-btn(icon="mdi-menu" outlined size="md" @click="openMenu")
  template(#title)
    .d-flex.align-center
      yz-icon.mr-2(icon="mdi-star" color="warning")
      .yz-heading-6 Custom Title
  template(#actions)
    .d-flex.align-center
      yz-btn(label="Draft" outlined @click="saveDraft")
      yz-btn(label="Publish" @click="publish")`
        }
      ]
    },
    fullscreenContentExamples() {
      return [
        {
          name: 'Default Content',
          id: 'default-content',
          description: 'Default content area with neutral background',
          label: 'Default Content',
          props: {},
          content: `Default content area with neutral background.
              This uses the default neutral-300 background color.`,
          code: `yz-dialog-fullscreen-content
  p Your content goes here
  p This uses the default neutral-300 background color`
        },
        {
          name: 'Custom Background',
          id: 'custom-background',
          description: 'Content area with custom background color',
          label: 'Custom Background',
          props: {
            color: '#0082ff'
          },
          content: `Content with custom hex background.
              The background is #0082ff.`,
          code: `yz-dialog-fullscreen-content(color="#0082ff")
  p Content with custom hex background
  p The background is #0082ff`
        },
        {
          name: 'YZ Color Variable',
          id: 'yz-color',
          description: 'Content using YZ design system color',
          label: 'YZ Color Variable',
          props: {
            color: 'yz-blue-100'
          },
          content: `Content with YZ color variable background.
              Using design system colors for consistency.`,
          code: `yz-dialog-fullscreen-content(color="yz-blue-100")
  p Content with YZ color variable background
  p Using design system colors for consistency`
        },
        {
          name: 'Shorthand Color',
          id: 'shorthand-color',
          description: 'Content using shorthand color name',
          label: 'Shorthand Color',
          props: {
            color: 'red-100'
          },
          content: `Content with shorthand color background.
              Automatically becomes var(--yz-red-100)`,
          code: `yz-dialog-fullscreen-content(color="red-100")
  p Content with shorthand color background
  p Automatically becomes var(--yz-red-100)`
        }
      ]
    },
    componentProps() {
      return [
        {
          name: 'v-model',
          type: 'Boolean',
          default: 'false',
          description: 'Controls the visibility of the dialog'
        },
        {
          name: 'persistent',
          type: 'Boolean',
          default: 'false',
          description: 'Prevents dialog from closing when clicking outside or pressing escape'
        },
        {
          name: 'fluid',
          type: 'Boolean',
          default: 'false',
          description: 'Makes dialog responsive to content width'
        },
        {
          name: 'scrollable',
          type: 'Boolean',
          default: 'false',
          description: 'Enables scrolling for content that exceeds viewport height'
        },
        {
          name: 'fullscreen',
          type: 'Boolean',
          default: 'false',
          description: 'Makes dialog cover the entire viewport (yz-dialog only)'
        },
        {
          name: 'width',
          type: 'String | Number',
          default: 'none',
          description: 'Width of the dialog. Can be set explicitly (e.g., "500", "800px")'
        },
        {
          name: 'max-width',
          type: 'String | Number',
          default: 'none',
          description: 'Maximum width of the dialog. Useful for responsive design'
        }
      ]
    },
    componentSlots() {
      return [
        {
          name: 'default',
          description: 'Main content of the dialog (usually yz-card with title, text, and actions)'
        },
        {
          name: 'activator',
          description: 'Element that triggers the dialog (when using slot-based activation)'
        }
      ]
    },
    useCases() {
      return [
        {
          scenario: 'Simple dialogs',
          approach: 'Use y-dialog with persistent and scrollable for basic content',
          example: 'y-dialog(v-model="show" persistent scrollable)'
        },
        {
          scenario: 'Custom width dialogs',
          approach: 'Use yz-dialog with explicit width and scrollable',
          example: 'yz-dialog(v-model="show" width="500" persistent scrollable)'
        },
        {
          scenario: 'Fullscreen dialogs',
          approach: 'Use yz-dialog with fullscreen for complex forms or content',
          example: 'yz-dialog(v-model="show" fullscreen persistent fluid scrollable)'
        },
        {
          scenario: 'Long content',
          approach: 'Always use scrollable prop for content that may exceed viewport',
          example: 'yz-dialog(v-model="show" width="500" persistent scrollable)'
        }
      ]
    },
    fullscreenHeaderProps() {
      return [
        {
          name: 'leftIcon',
          type: 'String',
          default: 'mdi-arrow-left',
          description: 'Left icon for back navigation. Set to null to hide the back button'
        },
        {
          name: 'title',
          type: 'String',
          default: 'null',
          description: 'Header title text'
        },
        {
          name: 'actionGap',
          type: 'String | Number',
          default: '12px',
          description: 'Gap between action buttons. Number values are converted to px'
        },
        {
          name: 'cancelActionLabel',
          type: 'String',
          default: 'Cancel',
          description: 'Label for cancel button'
        },
        {
          name: 'saveActionLabel',
          type: 'String',
          default: 'Save',
          description: 'Label for save button'
        },
        {
          name: 'loading',
          type: 'Boolean',
          default: 'false',
          description: 'Shows loading state on save button and disables cancel and back buttons'
        },
        {
          name: 'noActions',
          type: 'Boolean',
          default: 'false',
          description: 'Hides the action buttons section completely'
        },
        {
          name: 'disableBack',
          type: 'Boolean',
          default: 'false',
          description: 'Disables the back/left button independently'
        },
        {
          name: 'disableCancel',
          type: 'Boolean',
          default: 'false',
          description: 'Disables the cancel button independently'
        },
        {
          name: 'disableSave',
          type: 'Boolean',
          default: 'false',
          description: 'Disables the save button independently'
        }
      ]
    },
    fullscreenHeaderEvents() {
      return [
        {
          name: 'back',
          description: 'Emitted when left icon/back button is clicked. Also emits close event'
        },
        {
          name: 'cancel',
          description: 'Emitted when cancel action button is clicked. Also emits close event'
        },
        {
          name: 'save',
          description: 'Emitted when save action button is clicked. Also emits close event'
        },
        {
          name: 'close',
          description: 'Emitted after any action (back, cancel, save) for dialog state management'
        }
      ]
    },
    normalHeaderProps() {
      return [
        {
          name: 'title',
          type: 'String',
          default: 'null',
          description: 'Header title text'
        },
        {
          name: 'closeIcon',
          type: 'String',
          default: 'mdi-close',
          description: 'Icon for the close button'
        },
        {
          name: 'dividerColor',
          type: 'String',
          default: 'yz-neutral-300',
          description: 'Color of the divider line below the header'
        },
        {
          name: 'disableClose',
          type: 'Boolean',
          default: 'false',
          description: 'Disables the close button'
        }
      ]
    },
    normalHeaderEvents() {
      return [
        {
          name: 'close',
          description: 'Emitted when close button is clicked'
        }
      ]
    },
    normalActionsProps() {
      return [
        {
          name: 'saveLabel',
          type: 'String',
          default: 'Save',
          description: 'Label for save button'
        },
        {
          name: 'cancelLabel',
          type: 'String',
          default: 'Cancel',
          description: 'Label for cancel button'
        },
        {
          name: 'cancelActionUnderline',
          type: 'Boolean',
          default: 'false',
          description: 'Whether cancel button should be underlined'
        },
        {
          name: 'cancelActionColor',
          type: 'String',
          default: 'yz-neutral-700',
          description: 'Text color for cancel button'
        },
        {
          name: 'actionGap',
          type: 'String | Number',
          default: '12px',
          description: 'Gap between action buttons. Number values are converted to px'
        },
        {
          name: 'justify',
          type: 'String',
          default: 'end',
          description: 'Flex justify content. Options: space-between, space-around, space-evenly, center, start, end'
        },
        {
          name: 'loading',
          type: 'Boolean',
          default: 'false',
          description: 'Shows loading state on save button and disables cancel button'
        },
        {
          name: 'dividerColor',
          type: 'String',
          default: 'yz-neutral-300',
          description: 'Color of the divider line above actions'
        },
        {
          name: 'disableCancel',
          type: 'Boolean',
          default: 'false',
          description: 'Disables the cancel button independently'
        },
        {
          name: 'disableSave',
          type: 'Boolean',
          default: 'false',
          description: 'Disables the save button independently'
        }
      ]
    },
    normalActionsEvents() {
      return [
        {
          name: 'save',
          description: 'Emitted when save button is clicked'
        },
        {
          name: 'cancel',
          description: 'Emitted when cancel button is clicked'
        }
      ]
    },
    fullscreenContentProps() {
      return [
        {
          name: 'color',
          type: 'String',
          default: 'neutral-300',
          description: 'Background color. Accepts hex colors (e.g. "#F8FAFC"), full YZ variable names (e.g. "yz-blue-50"), or shorthand names (e.g. "red-50" becomes "var(--yz-red-50)")'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    },
    openDialog(dialogId) {
      this.$set(this.dialogs, dialogId, true)
    },
    closeDialog(dialogId) {
      this.$set(this.dialogs, dialogId, false)
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-2 {
  gap: 8px;
}
</style>